import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { flatten, maxBy, some, sortBy } from 'lodash'
import i18n from 'simple-react-i18n'
import AdministrationAction from '../../administration/actions/AdministrationAction'
import HomeAction from '../../home/actions/HomeAction'
import StationAction from '../../station/actions/StationAction'
import useApplicationSetting from '../../utils/customHook/useApplicationSetting'
import useLocalStorage from '../../utils/customHook/useLocalStorage'
import useProgressDispatch from '../../utils/customHook/useProgressDispatch'
import { DEFAULT_CONTRIBUTOR_TYPE, FILTER_TYPE } from '../../administration/components/user/constants/UserConstants'
import { SANDRE } from '../../referencial/constants/ReferencialConstants'
import { STATION_TYPE_CONSTANT, STATION_TYPE_NAME } from '../../station/constants/StationConstants'
import { disabledBorderInputColor, INDICATORS_COLORS, THRESHOLD_COLORS_CODES } from '../../utils/constants/ColorTheme'
import { getLabel } from '../../utils/StoreUtils'
import { getI18nOrLabel, getSandreLabel, normalizeString } from '../../utils/StringUtil'
import { getUnitProdMarker } from '../../utils/SvgUtil'
import { getBookmarks } from '../../utils/StationUtils'
import { getUserBookmarksByStationType } from '../../utils/UserUtil'
import { getYear } from '../../utils/DateUtil'
import moment from 'moment'
import { Chip } from '@mui/material'
import PropTypes from 'prop-types'
import useSandreList from 'utils/customHook/useSandreList'

export const NO_INDICATOR = -1
export const NORMAL = 0
export const VIGILANCE = 1
export const CRISIS = 2
export const NO_DATA = 3
const DEFAULT_INDICATORS = [NO_INDICATOR, NORMAL, VIGILANCE, CRISIS, NO_DATA]

const INDICATOR_CHIPS = [
    { value: CRISIS, i18nKey: 'crisis', backgroundColor: INDICATORS_COLORS.RED, fontColor: 'white' },
    { value: VIGILANCE, i18nKey: 'vigilance', backgroundColor: INDICATORS_COLORS.YELLOW, fontColor: 'black' },
    { value: NORMAL, i18nKey: 'normale', backgroundColor: INDICATORS_COLORS.BLUE, fontColor: 'white' },
    { value: NO_DATA, i18nKey: 'noData', backgroundColor: INDICATORS_COLORS.GREY, fontColor: 'white' },
    { value: NO_INDICATOR, i18nKey: 'notDetermined', backgroundColor: INDICATORS_COLORS.BLACK, fontColor: 'white' },
]

export const PRODUCTION_UNITS_HEADERS = [{ key: 'bookmarkIcon', value: '' }, { key: 'overrunChip', value: '' }, 'code', 'name', 'sector', 'city', 'begin', 'status', 'authorizedFlow', 'volumeAllowDay', 'max3hCapacity']
export const PRODUCTION_UNITS_EXPORT_HEADERS = ['bookmark', 'overrun', 'code', 'name', 'sector', 'city', 'begin', 'status', 'authorizedFlow', 'volumeAllowDay', 'max3hCapacity']

const SEARCH_VALUE_HEADERS = ['number', 'code', 'name', 'city', 'SISEeaucode', 'creationDate', 'stationTypeLabel', 'operator', 'administrator', 'referent', 'department']
const DEFAULT_UNIT_BLUE = '#0087FF'
const stationType = STATION_TYPE_NAME.productionUnit

const ColourfulChip = ({
    title,
    color = disabledBorderInputColor,
    fontColor = 'white',
}) => (
    <Chip
        label={title}
        size={'small'}
        sx={{ mr: 1, maxHeight: '16px', minHeight: '16px', backgroundColor: color, color: fontColor }}
    />
)

ColourfulChip.propTypes = {
    title: PropTypes.string,
    color: PropTypes.string,
    fontColor: PropTypes.string,
}

const getIndicatorObj = (unitIndicators) => {
    const indicatorsColors = unitIndicators.map(({ color }) => color)
    const joinedValues = unitIndicators.map(({ value }) => (value || '').toLowerCase()).join()
    if (!unitIndicators?.length) {
        return { color: 'white', value: NO_INDICATOR }
    } else if (indicatorsColors.some(color => [
        'red',
        'indianred',
        'darkmagenta',
        THRESHOLD_COLORS_CODES.RED.toLowerCase(),
        THRESHOLD_COLORS_CODES.INDIANRED.toLowerCase(),
        THRESHOLD_COLORS_CODES.DARK_MAGENTA.toLowerCase(),
    ].includes(color?.toLowerCase())) || joinedValues.includes('alerte')) {
        return { color: THRESHOLD_COLORS_CODES.RED, value: CRISIS }
    } else if (indicatorsColors.some(color => [
        'yellow',
        'lightyellow',
        'orange',
        THRESHOLD_COLORS_CODES.YELLOW.toLowerCase(),
        THRESHOLD_COLORS_CODES.LIGHT_YELLOW.toLowerCase(),
        THRESHOLD_COLORS_CODES.ORANGE.toLowerCase(),
    ].includes(color?.toLowerCase())) || joinedValues.includes('vigilance')) {
        return { color: THRESHOLD_COLORS_CODES.YELLOW, value: VIGILANCE }
    } else if (indicatorsColors.some(color => ['grey', 'gray'].includes(color?.toLowerCase()))) {
        return { color: THRESHOLD_COLORS_CODES.GREY, value: NO_DATA }
    }
    return { color: THRESHOLD_COLORS_CODES.BLUE, value: NORMAL }
}

// Filtres du tableau de bord des unités de production, partagés par tous les écrans « toutes unités ».
// Renvoie les unités mises en forme, les unités filtrées et le jeu de props de ProductionUnitFilterPanel.
const useProductionUnitsFilter = () => {
    const {
        cities,
        contributorLinks,
        contributors,
        globalResearch,
        productionUnits,
        productionUnitsAssociations,
        sandreCodes,
        sectors,
        selectedSearchValues,
        userBookmarks,
    } = useSelector(store => ({
        cities: store.CityReducer.cities,
        contributorLinks: store.StationReducer.contributorLinks,
        contributors: store.ContributorReducer.contributors,
        globalResearch: store.HomeReducer.globalResearch,
        productionUnits: store.ProductionUnitReducer.productionUnits,
        productionUnitsAssociations: store.ProductionUnitReducer.productionUnitsAssociations,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        sectors: store.SectorReducer.sectors,
        selectedSearchValues: store.AdministrationReducer.selectedSearchValues,
        userBookmarks: store.UserReducer.userBookmarks,
    }), shallowEqual)

    const [filter, setFilter] = useState(selectedSearchValues[stationType]?.filter || FILTER_TYPE.NOFILTER)
    const [filterResults, setFilterResults] = useState(selectedSearchValues[stationType]?.filterResults || [])
    const [searchValue, setSearchValue] = useState(globalResearch || selectedSearchValues[stationType]?.searchValue || '')
    const [adminFilter, setAdminFilter] = useState(globalResearch || selectedSearchValues[stationType]?.adminFilter || [])
    const [operatorFilter, setOperatorFilter] = useState(globalResearch || selectedSearchValues[stationType]?.operatorFilter || [])
    const [cityFilter, setCityFilter] = useState(globalResearch || selectedSearchValues[stationType]?.cityFilter || [])
    const [sectorFilter, setSectorFilter] = useState(globalResearch || selectedSearchValues[stationType]?.sectorFilter || [])
    const [indicators, setIndicators] = useState([])
    const [filterIndicators, setFilterIndicators] = useLocalStorage(`${stationType.toUpperCase()}_INDICATORS_FILTER`, DEFAULT_INDICATORS)

    const dispatch = useDispatch()

    useEffect(() => {
        if (globalResearch) {
            dispatch(HomeAction.updateGlobalResearch(''))
        }
    }, [])

    const { isLoaded, progress } = useProgressDispatch(() => {
        const associatedPiezos = productionUnitsAssociations.filter(as => as.stationLinkedType === STATION_TYPE_CONSTANT.piezometer).map(({ stationLinkedId }) => stationLinkedId)
        const associatedHydros = productionUnitsAssociations.filter(as => as.stationLinkedType === STATION_TYPE_CONSTANT.hydrometry).map(({ stationLinkedId }) => stationLinkedId)
        const associatedPluvios = productionUnitsAssociations.filter(as => as.stationLinkedType === STATION_TYPE_CONSTANT.pluviometry).map(({ stationLinkedId }) => stationLinkedId)

        return [
            dispatch(StationAction.fetchSpecificObservatoryFollowResult(associatedPiezos, STATION_TYPE_NAME.piezometer)).then(newIndicators => setIndicators(prev => [...prev, ...newIndicators])),
            dispatch(StationAction.fetchSpecificObservatoryFollowResult(associatedHydros, STATION_TYPE_NAME.hydrologicalStation)).then(newIndicators => setIndicators(prev => [...prev, ...newIndicators])),
            dispatch(StationAction.fetchSpecificObservatoryFollowResult(associatedPluvios, STATION_TYPE_NAME.pluviometer)).then(newIndicators => setIndicators(prev => [...prev, ...newIndicators])),
        ]
    }, [])

    const onChangeSearchValue = (value) => {
        setSearchValue(value)
        dispatch(AdministrationAction.setSelectedSearchValues(stationType, { searchValue: value }))
    }

    const getContributor = useCallback((up, ref, keyName) => {
        const now = moment().valueOf()
        const availableContributorLinks = contributorLinks.filter(cl => up.id === cl.idStation && cl.contributorType === ref && (!cl.endDate || cl.endDate > now)).map(c => ({ ...c, startDate: c.startDate ?? 0 }))
        const contributorLink = availableContributorLinks.length ? maxBy(availableContributorLinks, 'startDate') : undefined
        const contributor = contributorLink && contributors.find(c => contributorLink.idContributor === c.id)
        return contributor && {
            [keyName]: (contributor.mnemonique || contributor.name),
            [`${keyName}Code`]: contributor.id,
        } || {}
    }, [contributorLinks, contributors])

    const contributorsList = useSandreList(SANDRE.CONTRIBUTOR_TYPES)
    const operator = useApplicationSetting('contributorTypeOperator', d => parseInt(d)) || DEFAULT_CONTRIBUTOR_TYPE.OPERATOR
    const administrator = useApplicationSetting('contributorTypeAdministrator', d => parseInt(d)) || DEFAULT_CONTRIBUTOR_TYPE.ADMINISTRATOR

    const productionUnitsFormatted = useMemo(() => {
        if (productionUnits.length) {
            return sortBy(productionUnits, o => o.name ? o.name.toUpperCase() : '}').map(up => {
                const unitIndicators = indicators.filter(i => i.productionUnit === up.name).flatMap(indic => indic.data)
                const indicObj = getIndicatorObj(unitIndicators)
                const indicChipData = INDICATOR_CHIPS.find(indic => indic.value === indicObj.value)
                return {
                    ...up,
                    city: up.townCode && getLabel(cities, up.townCode, 'name'),
                    status: up.statusCode && getSandreLabel(sandreCodes, SANDRE.CODE_ETAT, up.statusCode),
                    begin: getYear(up.startDate),
                    ...getContributor(up, operator, contributorsList.find(c => c.code === operator)?.name),
                    ...getContributor(up, administrator, contributorsList.find(c => c.code === administrator)?.name),
                    nullValue: getBookmarks(up.code, getUserBookmarksByStationType(userBookmarks, 'productionUnit', up.code)),
                    bookmarkIcon: getBookmarks(up.code, getUserBookmarksByStationType(userBookmarks, 'productionUnit', up.code)),
                    bookmark: getBookmarks(up.code, getUserBookmarksByStationType(userBookmarks, 'productionUnit', up.code)) ? i18n.yes : i18n.no,
                    headers: PRODUCTION_UNITS_HEADERS,
                    indic: indicObj.value,
                    markerIcon: getUnitProdMarker(indicObj.color === 'white' ? DEFAULT_UNIT_BLUE : indicObj.color),
                    overrun: getI18nOrLabel(indicChipData?.i18nKey),
                    overrunChip: { value: <ColourfulChip title={getI18nOrLabel(indicChipData?.i18nKey)} color={indicChipData?.backgroundColor} fontColor={indicChipData?.fontColor} />, noPadding: true },
                    sector: up.sector && getLabel(sectors, up.sector),
                    sectorCode: up.sector,
                }
            })
        }
        return []
    }, [productionUnits, indicators, cities, sandreCodes, getContributor, operator, administrator, userBookmarks, sectors, contributorsList])

    const containsSearchValue = useCallback(
        (station) => some(SEARCH_VALUE_HEADERS, prop => station[prop]
            ? normalizeString(station[prop].toString()).includes(normalizeString(searchValue))
            : false)
        , [searchValue])

    const data = useMemo(() => {
        const administratorFiltered = adminFilter.length ? productionUnitsFormatted.filter(pdf => adminFilter.includes(pdf.buildingOwnerCode)) : productionUnitsFormatted
        const operatorFiltered = operatorFilter.length ? administratorFiltered.filter(pdf => operatorFilter.includes(pdf.operatorCode)) : administratorFiltered
        const cityFiltered = cityFilter?.length ? operatorFiltered.filter(of => cityFilter.includes(of.sectorCode)) : operatorFiltered
        const sectorFiltered = sectorFilter.length ? cityFiltered.filter(of => sectorFilter.includes(of.sectorCode)) : cityFiltered
        const searchFiltered = sectorFiltered.filter(s => containsSearchValue(s) && (filterIndicators.includes(s.indic)))

        const defaultResult = {
            title: i18n.productionUnits,
        }

        if (!filter || `${filter}` === `${FILTER_TYPE.NOFILTER}`) {
            return { ...defaultResult, stations: searchFiltered }
        } else if (`${filter}` === `${FILTER_TYPE.BOOKMARK}`) {
            const bookmarks = getUserBookmarksByStationType(userBookmarks, stationType)
            return { ...defaultResult, stations: flatten(bookmarks.map(code => searchFiltered.find(s => s.code.includes(code)) || [])) }
        }
        if (filterResults.length) {
            return { ...defaultResult, stations: searchFiltered.filter(sf => filterResults.some(fr => fr.id === sf.id)) }
        }
        return { ...defaultResult, stations: [] }
    }, [adminFilter, productionUnitsFormatted, operatorFilter, filter, filterResults, cityFilter, sectorFilter, containsSearchValue, filterIndicators, userBookmarks])

    const resetFilters = useCallback(() => {
        dispatch(AdministrationAction.setSelectedSearchValues(stationType, { searchValue: '' }))
        setSearchValue('')
        setFilter(FILTER_TYPE.NOFILTER)
        setFilterResults([])
        setAdminFilter([])
        setOperatorFilter([])
        setCityFilter([])
        setSectorFilter([])
        setFilterIndicators(DEFAULT_INDICATORS)
    }, [dispatch, setFilterIndicators])

    const filterPanelProps = {
        stationType,
        data: productionUnitsFormatted,
        onChangeSearchValue,
        setFilterResults,
        setFilter,
        defaultSearchValue: searchValue,
        defaultFilter: filter,
        defaultFilterResults: filterResults,
        defaultIndicators: filterIndicators,
        defaultAdmin: adminFilter,
        defaultOperator: operatorFilter,
        defaultCity: cityFilter,
        defaultSector: sectorFilter,
        setFilterIndicators,
        setAdmin: setAdminFilter,
        setOperator: setOperatorFilter,
        setCity: setCityFilter,
        setSector: setSectorFilter,
    }

    return {
        data,
        filterPanelProps,
        resetFilters,
        indicators,
        indicatorsLoaded: isLoaded,
        indicatorsProgress: progress,
        productionUnitsFormatted,
    }
}

export default useProductionUnitsFilter
